import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  GetFlags,
  CellFunction as _CellFunction,
  RESPONSE_NA,
  RESPONSE_NONE
} from '../../cells';
import {
  TriStateColor,
  TriStateLiteral,
  YesNoColor,
  YesNoLiteral
} from '../../../../../libs/session';
import {
  IEpxBasicNeeds,
  BasicNeedsModuleKey,
  BasicNeedsChildren
} from '../../../../../libs/apps/epxbasicneeds';

type CellFunction = _CellFunction<IEpxBasicNeeds>;

export const PrepareChild = <T extends BasicNeedsChildren>(
  session: IEpxBasicNeeds,
  child: T,
  key: BasicNeedsModuleKey
): JSX.Element | null => {
  const { parts } = session;
  let isRequired = false;
  if (parts) {
    for (const part of parts) {
      if (part.key === key) {
        isRequired = true;
        break;
      }
    }
  }
  if (!isRequired && !child) {
    return RESPONSE_NA;
  }
  if (!child || !child.respondedAt) {
    return RESPONSE_NONE;
  }
  return null;
};

export const CheckInCell: CellFunction = ({ row: { original: session } }) => {
  const checkin = session._checkin;
  const cell = PrepareChild(session, checkin, 'checkin');
  if (cell) {
    return cell;
  }
  return GetFlags(checkin, [
    (checkin) => {
      const { feeling } = checkin;
      if (feeling == null) {
        return ['Feeling: No Response', null];
      }
      return [`Feeling: ${TriStateLiteral[feeling]}`, TriStateColor[feeling]];
    }
  ]);
};

export const EducationCell: CellFunction = ({ row: { original: session } }) => {
  const education = session._education;
  const cell = PrepareChild(session, education, 'education');
  if (cell) {
    return cell;
  }
  return GetFlags(education, [
    (education) => {
      const { isInSchool } = education;
      if (isInSchool == null) {
        return ['In School: No Response', null];
      }
      return [`In School: ${YesNoLiteral(isInSchool)}`, YesNoColor(isInSchool)];
    }
  ]);
};

export const EmploymentCell: CellFunction = ({
  row: { original: session }
}) => {
  const employment = session._employment;
  const cell = PrepareChild(session, employment, 'employment');
  if (cell) {
    return cell;
  }
  return GetFlags(employment, [
    (employment) => {
      const { hadWorkTrouble } = employment;
      if (hadWorkTrouble == null) {
        return ['Work Trouble: No Response', null];
      }
      return [
        `Work Trouble: ${YesNoLiteral(hadWorkTrouble)}`,
        YesNoColor(!hadWorkTrouble)
      ];
    }
  ]);
};

export const FoodCell: CellFunction = ({ row: { original: session } }) => {
  const food = session._food;
  const cell = PrepareChild(session, food, 'food');
  if (cell) {
    return cell;
  }
  return GetFlags(food, [
    (food) => {
      const { numInadequateMeals } = food;
      return [
        `Monthly Inadequate Meals: ${
          numInadequateMeals == null ? 'No Response' : numInadequateMeals
        }`,
        null
      ];
    },
    (food) => {
      const { wantsFoodHelp } = food;
      if (!wantsFoodHelp) {
        return;
      }
      return ['Requested Food Help', 'intent-danger'];
    }
  ]);
};

export const InsuranceCell: CellFunction = ({ row: { original: session } }) => {
  const insurance = session._insurance;
  const cell = PrepareChild(session, insurance, 'insurance');
  if (cell) {
    return cell;
  }
  return GetFlags(insurance, [
    (insurance) => {
      const { hasHealthInsurance } = insurance;
      if (hasHealthInsurance == null) {
        return ['Has Insurance: No Response', null];
      }
      return [
        `Has Insurance: ${YesNoLiteral(hasHealthInsurance)}`,
        YesNoColor(hasHealthInsurance)
      ];
    }
  ]);
};

export const HousingCell: CellFunction = ({ row: { original: session } }) => {
  const housing = session._housing;
  const cell = PrepareChild(session, housing, 'housing');
  if (cell) {
    return cell;
  }
  return GetFlags(housing, [
    (housing) => {
      const { hasConcerns } = housing;
      if (hasConcerns == null) {
        return ['Has Concerns: No Response', null];
      }
      return [
        `Has Concerns: ${YesNoLiteral(hasConcerns)}`,
        YesNoColor(!hasConcerns)
      ];
    },
    (housing) => {
      const { wantsToTalk } = housing;
      if (wantsToTalk == null) {
        return ['Wanted to Talk: No Response', null];
      }
      return [
        `Wanted to Talk: ${YesNoLiteral(wantsToTalk)}`,
        YesNoColor(!wantsToTalk)
      ];
    }
  ]);
};

export const EpxBasicNeedsTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxBasicNeeds>[] = [
    {
      Header: 'Check In',
      id: '_checkin',
      Cell: ResponseGuard(CheckInCell)
    },
    {
      Header: 'Education',
      id: '_education',
      Cell: ResponseGuard(EducationCell)
    },
    {
      Header: 'Employment',
      id: '_employment',
      Cell: ResponseGuard(EmploymentCell)
    },
    {
      Header: 'Food',
      id: '_food',
      Cell: ResponseGuard(FoodCell)
    },
    {
      Header: 'Insurance',
      id: '_insurance',
      Cell: ResponseGuard(InsuranceCell)
    },
    {
      Header: 'Housing',
      id: '_housing',
      Cell: ResponseGuard(HousingCell)
    }
  ];
  return (
    <SessionTable<IEpxBasicNeeds>
      url="/epxbasicneeds/data/"
      columns={columns}
      {...props}
    />
  );
};

export default EpxBasicNeedsTable;
